import type {
  Activity,
  ApplicationFull,
  ApplicationListItem,
  Contact,
  ContactInput,
  ContactWithApps,
  FileItem,
  Me,
  Offer,
  OffersCompare,
  ReadinessItem,
  RecentActivity,
  Score,
  Settings,
  SettingsPatch,
  Stats,
  Today,
} from '@jt/shared';
import type { Stage } from '@jt/core';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError, del, get, patch, post, put, qs } from './client';

/* ---------- ключи кэша ---------- */

export const keys = {
  me: ['me'] as const,
  apps: (f?: AppFilter) => (f ? (['applications', f] as const) : (['applications'] as const)),
  app: (id: string) => ['application', id] as const,
  today: ['today'] as const,
  stats: ['stats'] as const,
  offers: ['offers'] as const,
  settings: ['settings'] as const,
  readiness: ['readiness'] as const,
  contacts: (q = '') => ['contacts', q] as const,
  files: (kind?: string) => ['files', kind ?? 'all'] as const,
  recent: ['activities-recent'] as const,
  prep: (id: string) => ['prep', id] as const,
};

export interface AppFilter {
  q?: string;
  source?: string;
  trash?: boolean;
}

/* ---------- запросы ---------- */

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return await get<Me>('/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: Infinity,
    retry: false,
  });
}

export function useApplications(f: AppFilter = {}) {
  // пустые фильтры не меняют ключ кэша — список без фильтров запрашивается один раз
  const norm: AppFilter = {
    q: f.q?.trim() || undefined,
    source: f.source || undefined,
    trash: f.trash || undefined,
  };
  return useQuery({
    queryKey: keys.apps(norm),
    queryFn: ({ signal }) =>
      get<ApplicationListItem[]>(
        `/applications${qs({ q: norm.q, source: norm.source, trash: norm.trash ? 'true' : undefined })}`,
        signal,
      ),
    placeholderData: (prev) => prev,
  });
}

export const useApplication = (id: string | null | undefined) =>
  useQuery({
    queryKey: keys.app(id ?? ''),
    queryFn: ({ signal }) => get<ApplicationFull>(`/applications/${id}`, signal),
    enabled: !!id,
    retry: (n, e) => !(e instanceof ApiError && e.status === 404) && n < 2,
  });

export const useToday = () => useQuery({ queryKey: keys.today, queryFn: () => get<Today>('/today') });
export const useStats = () => useQuery({ queryKey: keys.stats, queryFn: () => get<Stats>('/stats') });
export const useOffersCompare = () =>
  useQuery({ queryKey: keys.offers, queryFn: () => get<OffersCompare>('/offers/compare') });
export const useSettings = () =>
  useQuery({ queryKey: keys.settings, queryFn: () => get<Settings>('/settings') });
export const useReadiness = () =>
  useQuery({ queryKey: keys.readiness, queryFn: () => get<ReadinessItem[]>('/readiness') });
export const useContacts = (q = '') =>
  useQuery({
    queryKey: keys.contacts(q),
    queryFn: ({ signal }) => get<ContactWithApps[]>(`/contacts${qs({ q: q.trim() })}`, signal),
    placeholderData: (prev) => prev,
  });
export const useFiles = (kind?: 'resume' | 'attachment' | 'link') =>
  useQuery({ queryKey: keys.files(kind), queryFn: () => get<FileItem[]>(`/files${qs({ kind })}`) });
export const useRecent = () =>
  useQuery({ queryKey: keys.recent, queryFn: () => get<RecentActivity[]>('/activities/recent') });

/** «Сегодня» по часовому поясу пользователя — берётся с сервера. */
export function useTodayDate(): string {
  const t = useToday();
  return t.data?.today ?? new Date().toISOString().slice(0, 10);
}

/* ---------- обновление кэша после изменений ---------- */

function toListItem(f: ApplicationFull): ApplicationListItem {
  const { score: _s, activities: _a, files: _f, ...rest } = f;
  return rest;
}

/** Полная карточка из ответа сервера → кэш карточки и всех списков, плюс пересчёт сводок. */
export function applyFull(qc: QueryClient, full: ApplicationFull) {
  qc.setQueryData(keys.app(full.id), full);
  let found = false;
  qc.setQueriesData<ApplicationListItem[]>({ queryKey: keys.apps() }, (list) => {
    if (!list) return list;
    const i = list.findIndex((x) => x.id === full.id);
    if (i < 0) return list;
    found = true;
    const next = [...list];
    next[i] = toListItem(full);
    return next;
  });
  if (!found || full.deletedAt) void qc.invalidateQueries({ queryKey: keys.apps() });
  refreshSummaries(qc);
}

export function refreshSummaries(qc: QueryClient) {
  for (const k of [keys.today, keys.stats, keys.offers, keys.recent, ['contacts'], ['files']])
    void qc.invalidateQueries({ queryKey: k });
}

/**
 * Очередь сохранений по каждой вакансии: изменения уходят по одному и всегда с последней версией
 * из кэша — быстрые правки подряд не дают ложных конфликтов.
 */
const chains = new Map<string, Promise<unknown>>();
function chain<T>(id: string, fn: () => Promise<T>): Promise<T> {
  const prev = chains.get(id) ?? Promise.resolve();
  const next = prev.catch(() => undefined).then(fn);
  chains.set(
    id,
    next.catch(() => undefined),
  );
  return next;
}

const versionOf = (qc: QueryClient, id: string) =>
  qc.getQueryData<ApplicationFull>(keys.app(id))?.version ??
  qc
    .getQueriesData<ApplicationListItem[]>({ queryKey: keys.apps() })
    .flatMap(([, l]) => l ?? [])
    .find((x) => x.id === id)?.version;

export type AppPatch = Partial<
  Pick<
    ApplicationFull,
    | 'company'
    | 'role'
    | 'url'
    | 'source'
    | 'location'
    | 'format'
    | 'salaryRange'
    | 'appliedAt'
    | 'nextStep'
    | 'nextDate'
    | 'nextTime'
    | 'rejectionReason'
    | 'notes'
    | 'position'
  >
>;

export function useAppActions() {
  const qc = useQueryClient();
  const done = (full: ApplicationFull) => {
    applyFull(qc, full);
    return full;
  };
  const onConflict = async (id: string, e: unknown) => {
    if (e instanceof ApiError && e.status === 409) await qc.invalidateQueries({ queryKey: keys.app(id) });
    throw e;
  };
  const run = (id: string, fn: () => Promise<ApplicationFull>) =>
    chain(id, fn).then(done, (e) => onConflict(id, e));

  return {
    patch: (id: string, fields: AppPatch) =>
      run(id, () => patch<ApplicationFull>(`/applications/${id}`, { ...fields, version: versionOf(qc, id) })),
    stage: (id: string, stage: Stage) =>
      run(id, () => post<ApplicationFull>(`/applications/${id}/stage`, { stage, source: 'manual' })),
    stepDone: (id: string) => run(id, () => post<ApplicationFull>(`/applications/${id}/next-step/done`)),
    remove: (id: string) => run(id, () => del<ApplicationFull>(`/applications/${id}`)),
    restore: (id: string) => run(id, () => post<ApplicationFull>(`/applications/${id}/restore`)),
    linkContact: (id: string, body: { contactId: string } | { contact: ContactInput }) =>
      run(id, () => post<ApplicationFull>(`/applications/${id}/contacts`, body)),
    unlinkContact: (id: string, contactId: string) =>
      run(id, () => del<ApplicationFull>(`/applications/${id}/contacts/${contactId}`)),
    attachFile: (id: string, fileId: string) =>
      run(id, () => post<ApplicationFull>(`/applications/${id}/files`, { fileId })),
    detachFile: (id: string, fileId: string) =>
      run(id, () => del<ApplicationFull>(`/applications/${id}/files/${fileId}`)),
    /** Поиск контакта в интернете: долгий (до минуты) — вне очереди правок карточки. */
    findContacts: (id: string) =>
      post<ApplicationFull>(`/applications/${id}/contact-search`).then(done, (e) => onConflict(id, e)),
    acceptSuggestion: (id: string, index: number) =>
      run(id, () => post<ApplicationFull>(`/applications/${id}/contact-search/${index}/accept`)),
    dismissSuggestion: (id: string, index: number) =>
      run(id, () => post<ApplicationFull>(`/applications/${id}/contact-search/${index}/dismiss`)),
    /** После изменений истории/оффера/контакта — перечитать карточку. */
    reload: async (id: string) => {
      const full = await get<ApplicationFull>(`/applications/${id}`);
      return done(full);
    },
    addActivity: (
      id: string,
      body: { occurredOn: string; channel: string; direction: string; text: string; raw?: string },
    ) =>
      chain(id, async () => {
        await post<Activity>(`/applications/${id}/activities`, body);
        return done(await get<ApplicationFull>(`/applications/${id}`));
      }),
    deleteActivity: (id: string, activityId: string) =>
      chain(id, async () => {
        await del(`/activities/${activityId}`);
        return done(await get<ApplicationFull>(`/applications/${id}`));
      }),
    offer: (id: string, fields: Partial<Omit<Offer, 'version'>>) =>
      chain(id, async () => {
        const cur = qc.getQueryData<ApplicationFull>(keys.app(id))?.offer;
        const r = await put<{ offer: Offer; score: Score }>(`/applications/${id}/offer`, {
          ...fields,
          ...(cur ? { version: cur.version } : {}),
        });
        const full = qc.getQueryData<ApplicationFull>(keys.app(id));
        if (full) qc.setQueryData(keys.app(id), { ...full, offer: r.offer, score: r.score });
        void qc.invalidateQueries({ queryKey: keys.apps() });
        refreshSummaries(qc);
        return r;
      }).catch((e) => onConflict(id, e)),
    /** Правка контакта из карточки: версия берётся из кэша в момент отправки. */
    patchContact: (appId: string, contactId: string, fields: Partial<ContactInput>) =>
      chain(appId, async () => {
        const cur = qc
          .getQueryData<ApplicationFull>(keys.app(appId))
          ?.contacts.find((c) => c.id === contactId);
        await patch<Contact>(`/contacts/${contactId}`, { ...fields, version: cur?.version ?? 1 });
        return done(await get<ApplicationFull>(`/applications/${appId}`));
      }).catch((e) => onConflict(appId, e)),
  };
}

export function useCreateApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => post<ApplicationFull>('/applications', body),
    onSuccess: (full) => {
      qc.setQueryData(keys.app(full.id), full);
      void qc.invalidateQueries({ queryKey: keys.apps() });
      refreshSummaries(qc);
    },
  });
}

export function useSettingsSave() {
  const qc = useQueryClient();
  return (fields: Omit<SettingsPatch, 'version'>) => {
    const cur = qc.getQueryData<Settings>(keys.settings);
    return chain('__settings', () =>
      patch<Settings>('/settings', { ...fields, version: cur?.version ?? 1 }),
    ).then(
      (s) => {
        qc.setQueryData(keys.settings, s);
        if ('name' in fields) void qc.invalidateQueries({ queryKey: keys.me });
        refreshSummaries(qc);
        return s;
      },
      async (e) => {
        if (e instanceof ApiError && e.status === 409)
          await qc.invalidateQueries({ queryKey: keys.settings });
        throw e;
      },
    );
  };
}

export function invalidateAll(qc: QueryClient) {
  void qc.invalidateQueries();
}

/** Текущий пользователь — администратор (ключи сервисов, пользователи, приглашения). */
export function useIsAdmin(): boolean {
  return useMe().data?.user.role === 'admin';
}
